import { forwardRef, Fragment, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { BlockContent } from '~/components/BlockContent'

import WordTooltip from '~/components/Tooltip/WordTooltip'
import { useAppSelector } from '~/redux/hooks'

import { Link } from '~/components'

import { selectBootcampPageUsefulWords } from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'

import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'
import bootcampStyle from '~/shared/styles/BootcampSanityBlock.module.css'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ExclamationCircleStrokeIcon from '~/components/Icons/svgs/ExclamationCircleStrokeIcon'
import TimesIcon from '~/components/Icons/svgs/TimesIcon'

const UsefulWords = (): React.ReactElement => {
  const [showTooltip, setShowTooltip] = useState<string | undefined>()
  const { locale: language } = useRouter()
  const usefulWords = useAppSelector(selectBootcampPageUsefulWords)
  const { hex }: CategoryColorClasses = useAppSelector(selectVideoTopicColor)

  const closeTooltip = (): void => setShowTooltip(undefined)

  const sortedUsefulWords = useMemo(() => {
    // eslint-disable-next-line fp/no-mutating-methods
    return [...usefulWords]
      .map((item) => item || {})
      ?.sort((wordA, wordB) => wordA?.term?.localeCompare(wordB?.term, language, { ignorePunctuation: true }))
  }, [language, usefulWords])

  const usefulWordsCol = sortedUsefulWords.length > 9 ? 2 : 1
  const middleIndex = Math.ceil(sortedUsefulWords.length / usefulWordsCol)
  const style = usefulWordsCol === 1 ? 'grid-cols-1' : 'grid-cols-2 gap-4'

  return (
    <div className={`grid ${style}`} role="definition">
      {new Array(usefulWordsCol).fill('').map((word, index) => (
        <Fragment key={`${word + index}`}>
          <ul onMouseOut={closeTooltip}>
            {sortedUsefulWords.slice(!index ? 0 : middleIndex, !index ? middleIndex : sortedUsefulWords.length).map(
              ({ term, definition, link }) =>
                term && (
                  <li className="mb-0.5 last:mb-0 cursor-default" data-testid={`usefulWordsCol${index + 1}`} key={term}>
                    {link ? (
                      <Link to={link}>
                        <Label data={term} />
                      </Link>
                    ) : (
                      <WordTooltip
                        arrow
                        color={hex}
                        onClick={() => setShowTooltip(term)}
                        onMouseEnter={() => setShowTooltip(term)}
                        open={showTooltip === term}
                        placement={!index ? 'top-start' : 'top-end'}
                        PopperProps={{
                          disablePortal: true,
                        }}
                        title={definition ? <Content data={definition} onClose={closeTooltip} /> : ''}
                      >
                        <Label data={term} />
                      </WordTooltip>
                    )}
                  </li>
                ),
            )}
          </ul>
        </Fragment>
      ))}
    </div>
  )
}

// eslint-disable-next-line react/display-name
const Label = forwardRef<HTMLDivElement, { data: string }>((props, ref): React.ReactElement => {
  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)
  return (
    <div {...props} className="flex w-fit items-center not-italic text-xs leading-4 mb-2.5" ref={ref}>
      <span className="rounded-full h-5 flex items-center bg-transparent filter drop-shadow-xl">
        <SVGIcon icon={ExclamationCircleStrokeIcon} pathClassName={colorInfo.textClass} />
      </span>
      <span className="ml-1">{props.data}</span>
    </div>
  )
})

const Content = ({ data, onClose }: { data?: unknown[]; onClose: () => void }): React.ReactElement => {
  return (
    <div className="relative p-1.5 pr-5 leading-tight">
      <button className="absolute right-1 top-0.5 lg:hidden" onClick={onClose}>
        <SVGIcon icon={TimesIcon} pathClassName="text-white" size={10} />
      </button>
      <BlockContent blocks={data || []} className={bootcampStyle['bootcamp-block-content']} />
    </div>
  )
}

export default UsefulWords
